// ============================================================
// bill-import.js
// ------------------------------------------------------------
// ข้อกำหนดของไฟล์ Excel "การแจ้งหนี้และจัดเก็บค่าน้ำ" + สูตรสร้างบิล 1 แถว
// ใช้ร่วมกัน 2 หน้า:
//   - ImportBillWater     นำเข้าแบบมีเลขมาตรเดือนนี้/เดือนก่อน (คำนวณหน่วยที่ใช้เอง)
//   - ImportBillUnitUsed  นำเข้าแบบระบุหน่วยที่ใช้มาตรง ๆ
//
// เดิมสองหน้าคำนวณยอดบิลด้วยสูตรที่เขียนแยกกัน และมีจุดต่างเล็ก ๆ ที่ทำให้ยอดไม่ตรง
// (หน้าหนึ่งใส่ || 0 กันค่าว่าง อีกหน้าไม่ใส่ ทำให้ได้ NaN เมื่อช่องในไฟล์ว่าง)
// ตอนนี้ใช้สูตรเดียวกันทั้งคู่
// ============================================================

import { roundToTwo, format_date } from "../core/format.js";
import { calcCommonExpenses, billTotal } from "./water-setting.js";

// ชื่อชีตในไฟล์ template
export const WATER_BILL_SHEET_NAME = "การแจ้งหนี้และจัดเก็บค่าน้ำ";

// หัวคอลัมน์ตามลำดับในไฟล์ template
export const WATER_BILL_COLUMNS = [
  "บ้านเลขที่",
  "ชื่อ-นามสกุล",
  "วันที่จด",
  "วันที่",
  "เลขมาตรเดือนนี้",
  "เลขมาตรเดือนก่อน",
  "หน่วยที่ใช้",
  "รายได้อื่น ๆ",
  "เพิ่มหนี้",
  "หมายเหตุเพิ่มหนี้",
  "ลดหนี้",
  "หมายเหตุลดหนี้",
  "ค่าเคเบิล",
  "ค่าประกันอัคคีภัย",
];

// แถวตัวอย่างในไฟล์ template ที่ต้องไม่ถูกนำเข้า
export const SAMPLE_ROW_NAME = "ตัวอย่าง ข้อมูล";

// อ่านตัวเลขจากช่องใน Excel — ช่องว่าง/ไม่ใช่ตัวเลข ให้เป็น 0 (กันยอดรวมกลายเป็น NaN)
const num = (value) => Number(value || 0) || 0;

// สร้างบิล 1 แถวจากข้อมูล 1 บรรทัดในไฟล์ Excel
//   item        = 1 บรรทัดจากไฟล์
//   setting     = ตั้งค่าโครงการ (equipment_maintenance_fee, waste_cost, common_expenses, common_expenses_type)
//   customer    = ข้อมูลลูกค้าของบ้านเลขที่นั้น (ใช้ room_size ตอนคิดค่าส่วนกลางตามขนาดห้อง)
//   water_bill  = ค่าน้ำที่คำนวณมาแล้วจากสูตรของโครงการ
//   gauge       = { current_gauge_number, last_gauge_number, unit_used }
export const buildBillRow = ({ item, setting, customer, water_bill, gauge }) => {
  const cable_cost = roundToTwo(num(item["ค่าเคเบิล"]));
  const fire_cost = roundToTwo(num(item["ค่าประกันอัคคีภัย"]));
  const other_income = num(item["รายได้อื่น ๆ"]);
  const add_debt = num(item["เพิ่มหนี้"]);
  const reduce_debt = num(item["ลดหนี้"]);
  const common_expenses = calcCommonExpenses(setting.common_expenses_type, setting.common_expenses, customer?.room_size);

  const row = {
    address_number: String(item["บ้านเลขที่"] ?? "").trim(),
    full_name: item["ชื่อ-นามสกุล"]?.toString()?.trim() || "",
    date_of_note: item["วันที่"] ? format_date(item["วันที่"], "yyyy-mm-dd", "en") : null,
    current_gauge_number: gauge.current_gauge_number,
    last_gauge_number: gauge.last_gauge_number,
    unit_used: gauge.unit_used,
    water_bill,
    equipment_maintenance_fee: setting.equipment_maintenance_fee,
    common_expenses,
    waste_cost: setting.waste_cost,
    cable_cost,
    other_income,
    fire_cost,
    add_debt,
    reduce_debt,
    remark_add_debt: item["หมายเหตุเพิ่มหนี้"],
    remark_reduce_debt: item["หมายเหตุลดหนี้"],
  };

  // ยอดรวม = ค่าน้ำ + ค่าใช้จ่ายอื่นทั้งหมด - ลดหนี้ (สูตรเดียวกับฝั่ง backend)
  row.total_amount = billTotal(row);
  return row;
};

// ตัดแถวตัวอย่างในไฟล์ template ออก (อยู่บรรทัดแรกเสมอ)
export const dropSampleRow = (data) => {
  if (data.length && data[0].full_name === SAMPLE_ROW_NAME) data.shift();
  return data;
};
